// ============================================================================
// RUNTIME CONFIG ORCHESTRATOR (issues #49 / #53)
// ============================================================================
// Single source of truth for the *runtime* tenant configuration consumed by
// `theme.ts`, `appsConfig.ts` and `schemaConfig.ts`. It keeps an in-memory copy
// of the public payload and exposes SYNCHRONOUS getters so those modules can
// keep their existing build-time export API while sourcing data from runtime.
//
// Lifecycle (driven by `main.tsx`):
//   1. `ensureRuntimeConfigReady()` — runs BEFORE the app modules are imported.
//      Loads the cached payload (instant) or, on a cold start with no cache,
//      attempts one short fetch so the first paint already uses runtime data.
//   2. The config modules read `getRuntimeTheme()/Apps()/Schema()`, falling back
//      to build-time JSON when the runtime payload is missing.
//   3. `startBackgroundRevalidation()` — stale-while-revalidate: re-checks the
//      API periodically / on focus. A new `version` triggers a one-time reload
//      so edits in the admin panel apply WITHOUT a redeploy.
// ============================================================================

import type { ThemePreset } from "../theme";
import type { AppsPreset } from "../apps/types";
import type { SchemaOverridesPreset } from "../schemas/types";
import type { PublicTenantConfig } from "./types";
import {
  fetchTenantConfig,
  isTenantConfigClientEnabled,
  tenantCacheKey,
} from "./tenantConfigClient";
import {
  isFresh,
  readCache,
  writeCache,
  REVALIDATE_INTERVAL_MS,
} from "./configCache";
import { getCompanyAppsOverride } from "./companyAppsOverride";

// How long the cold-start fetch may block first paint before falling back to
// build-time config. Kept short so a slow/unreachable API never hangs boot.
const FIRST_FETCH_TIMEOUT_MS = 3500;

// ---------------------------------------------------------------------------
// In-memory state
// ---------------------------------------------------------------------------
let current: PublicTenantConfig | null = null;
// Version the app modules booted with (null = booted on build-time fallback).
let bootVersion: number | null = null;
let bootResolved = false;

let revalidating = false;
let listenersAttached = false;
let reloadScheduled = false;

// ---------------------------------------------------------------------------
// Synchronous getters consumed by the config modules
// ---------------------------------------------------------------------------

/** Runtime theme preset, or null to fall back to build-time. */
export function getRuntimeTheme(): ThemePreset | null {
  return current?.theme ?? null;
}

/**
 * Runtime apps preset, or null to fall back to build-time. The canonical
 * top-level `connectionType` is folded into the preset when the preset itself
 * does not carry one, so `getConnectionType()` keeps working unchanged.
 */
export function getRuntimeApps(): AppsPreset | null {
  // Per-company apps override (reseller company switch) wins over the reseller's
  // own apps, but the rest of the runtime config (theme/schema) is untouched, so
  // only the available apps change — not the look-and-feel.
  const apps = getCompanyAppsOverride() ?? current?.apps;
  if (!apps) return null;
  if (current?.connectionType && apps.connectionType == null) {
    return { ...apps, connectionType: current.connectionType };
  }
  return apps;
}

/** Runtime frontend schema preset, or null to fall back to build-time. */
export function getRuntimeSchema(): SchemaOverridesPreset | null {
  return current?.frontendSchema ?? null;
}

/** The full runtime payload currently in memory (or null). */
export function getRuntimeConfig(): PublicTenantConfig | null {
  return current;
}

// ---------------------------------------------------------------------------
// Boot hydration
// ---------------------------------------------------------------------------

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(null);
      });
  });
}

/**
 * Make the runtime config available before the app modules are imported.
 * Cache-first; only a cold start with no cache blocks (briefly) on the network.
 * Always resolves — never rejects — so the app boots even fully offline.
 */
export async function ensureRuntimeConfigReady(): Promise<void> {
  if (bootResolved) return;

  if (!isTenantConfigClientEnabled()) {
    // No API configured → pure build-time mode.
    bootVersion = null;
    bootResolved = true;
    return;
  }

  const key = tenantCacheKey();
  const cached = readCache(key);

  if (cached) {
    // Serve cache instantly; revalidation refreshes it in the background.
    current = cached.payload;
  } else {
    // Cold start: try once to boot directly from runtime, bounded by a timeout.
    const result = await withTimeout(fetchTenantConfig(), FIRST_FETCH_TIMEOUT_MS);
    if (result && result.status === "ok") {
      current = result.payload;
      writeCache(key, result.payload, result.etag);
    }
    // On error/timeout: leave `current` null → build-time fallback.
  }

  bootVersion = current?.version ?? null;
  bootResolved = true;
}

// ---------------------------------------------------------------------------
// Background revalidation (stale-while-revalidate)
// ---------------------------------------------------------------------------

/**
 * Re-check the API and refresh the cache. Skips the network while the cache is
 * still fresh. A version change is applied via a one-time reload so the running
 * app picks up admin edits without a redeploy.
 */
export async function revalidateRuntimeConfig(force = false): Promise<void> {
  if (revalidating || !isTenantConfigClientEnabled()) return;

  const key = tenantCacheKey();
  const cached = readCache(key);
  if (!force && cached && isFresh(cached)) return;

  revalidating = true;
  try {
    const result = await fetchTenantConfig({ etag: cached?.etag ?? null });

    if (result.status === "not-modified" && cached) {
      // Refresh the freshness timestamp so we stop re-checking for a while.
      writeCache(key, cached.payload, cached.etag);
      return;
    }

    if (result.status === "ok") {
      writeCache(key, result.payload, result.etag);
      current = result.payload;
      maybeApplyVersionChange(result.payload.version);
    }
  } finally {
    revalidating = false;
  }
}

/**
 * Reload the page once when the live config version differs from the one the
 * app booted with, so theme/apps/schema re-hydrate from the new cached payload.
 * Guarded against reload loops (the post-reload boot reads the new version, then
 * revalidation sees a matching version and does nothing).
 */
function maybeApplyVersionChange(newVersion: number): void {
  if (reloadScheduled) return;
  if (bootVersion !== null && newVersion === bootVersion) return;

  reloadScheduled = true;
  if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
    // eslint-disable-next-line no-console
    console.info(
      `[tenantConfig] runtime config changed (v${bootVersion} → v${newVersion}); reloading to apply.`,
    );
    window.location.reload();
  }
}

/**
 * Kick off background revalidation: an immediate check (covers a stale cache
 * used for first paint) plus periodic / focus / reconnection re-checks.
 */
export function startBackgroundRevalidation(): void {
  if (!isTenantConfigClientEnabled()) return;

  void revalidateRuntimeConfig();

  if (listenersAttached || typeof window === "undefined") return;
  listenersAttached = true;

  setInterval(() => void revalidateRuntimeConfig(), REVALIDATE_INTERVAL_MS);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void revalidateRuntimeConfig();
    }
  });
  window.addEventListener("online", () => void revalidateRuntimeConfig());
}
